// Batch 0 (UX/IA overhaul) — the shared "camera reference" shape.
//
// This is the ONE structure every camera-bearing list/detail is meant to
// consume so the Site → NVR → Channel hierarchy is visible everywhere, not
// just on the Topology tab. Batch 0 defines the type; the backend read-path
// that populates `site_name` / `source_id` / `nvr_*` lands in Batch 1 (a soft
// join from `cameras` to the topology Source — no schema migration, ingest
// untouched). Until then, the optional fields are simply absent and the
// hierarchy-aware components degrade gracefully (they omit missing segments).
//
// Keep this MINIMAL and additive: it is a *reference* to a camera plus its
// parentage, not the full Camera record. Pages already holding a richer
// camera object can pass it straight in — extra fields are ignored.

export interface CameraRef {
  /** Logical camera id (cameras.id). */
  id: string;
  /** Human name. Falls back to a short id if absent (see shortId). */
  name?: string | null;

  // --- physical parentage (Site → Node/agent → Source/NVR → Channel) ---
  site_id?: string | null;
  site_name?: string | null;
  /** SLink edge agent (Node) id, if reached via an agent. */
  agent_id?: string | null;
  agent_name?: string | null;
  /**
   * Topology Source (NVR/DVR) id. Batch-1 soft-join target — NOT a hard FK on
   * the cameras table today. Absent until the read-path lands.
   */
  source_id?: string | null;
  nvr_make?: string | null;
  nvr_model?: string | null;
  nvr_serial?: string | null;
  /** 1-based channel this camera occupies on its NVR/DVR. */
  channel_no?: number | null;
}

/** Canonical last-resort id truncation. Use ONLY when no name resolves — never
 *  as the primary label. Standardizes the ad-hoc `id.slice(0,8)…` scattered
 *  across Alerts / Detections / Evidence today. */
export function shortId(id: string | null | undefined): string {
  if (!id) return "—";
  return id.length <= 8 ? id : `${id.slice(0, 8)}…`;
}
